import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    className?: string;
}

const FOCUSABLE_SELECTORS =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const Modal = ({ open, onClose, children, className }: ModalProps) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!open) return;

        previouslyFocused.current = document.activeElement as HTMLElement;

        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
            FOCUSABLE_SELECTORS,
        );
        const first = focusable?.[0];
        const last = focusable?.[focusable.length - 1];

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            } else if (e.key === "Tab" && focusable?.length) {
                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault();
                        (last || first)?.focus();
                    }
                } else {
                    if (document.activeElement === last) {
                        e.preventDefault();
                        (first || last)?.focus();
                    }
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        first?.focus();

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            previouslyFocused.current?.focus();
        };
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return;

        const original = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = original;
        };
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-black/50"
                tabIndex={-1}
            />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                className={cn(
                    "relative bg-black rounded-lg overflow-hidden w-full max-w-3xl", // default style
                    className,
                )}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white"
                    aria-label="Close"
                >
                    <X className="h-6 w-6" />
                </button>
                {children}
            </div>
        </div>
    );
};

export default Modal;

